import { StoreDispatch } from 'interfaces/component'
import FeedPostCommentList, {
  FeedPostCommentListOwnProps,
  FeedPostCommentListProps,
} from 'components/features/socialFeed/FeedPostCommentList/FeedPostCommentList'
import { createFeedAllCommentUidsByPostUidSelector, feedPostSelector, hiddenRepliesSelector } from 'selectors/feedSelectors'
import { createSelector } from 'reselect'
import { connectOptionallyAdvanced, OptionalRendererProps, createOptionalRenderer } from 'tangle-shared'
import { CommentUid, EditingCommentUids } from 'interfaces/feed'
import { State } from 'interfaces/state'
import { getCommentsActionCreator } from 'actions/feedActions'
import { CursorPaginatable } from 'interfaces/table'

type InterimStateProps = {
  nextItemIdsPagination: CursorPaginatable<CommentUid> | null
  commentsCount: number
  isLoading: boolean
  hiddenRepliesUids: EditingCommentUids
}


const selectorFactory = (dispatch: StoreDispatch) => {
  let prevInterimStateProps: InterimStateProps
  let prevProps: OptionalRendererProps<FeedPostCommentListProps>
  const optionalRenderer = createOptionalRenderer<FeedPostCommentListProps>()

  const interimStatePropsSelector = createSelector(
    createFeedAllCommentUidsByPostUidSelector(),
    feedPostSelector,
    (state: State, { postUid }: FeedPostCommentListOwnProps) => Boolean(state.app.feed.postCommentsLoadingIds[postUid]),
    hiddenRepliesSelector,
    (nextItemIdsPagination, post, isLoading, hiddenRepliesUids): InterimStateProps | null => {
      if (!post) {
        return null
      }

      return {
        nextItemIdsPagination,
        commentsCount: post.comments_count,
        isLoading,
        hiddenRepliesUids,
      }
    }
  )

  return (nextState: State, nextOwnProps: FeedPostCommentListOwnProps): OptionalRendererProps<FeedPostCommentListProps> => {
    const nextInterimStateProps = interimStatePropsSelector(nextState, nextOwnProps)
    if (!nextInterimStateProps) {
      return (prevProps = optionalRenderer.fallback())
    }

    const { nextItemIdsPagination, commentsCount, isLoading, hiddenRepliesUids } = nextInterimStateProps
    if (!nextItemIdsPagination) {
      return (prevProps = optionalRenderer.fallback())
    }

    if (prevInterimStateProps === nextInterimStateProps) {
      return prevProps
    }

    prevInterimStateProps = nextInterimStateProps
    const { results, pagination } = nextItemIdsPagination
    const isLoadMoreShown = (!pagination && results.length < commentsCount) || Boolean(pagination && pagination.next)

    const { postUid } = nextOwnProps

    prevProps = optionalRenderer.render({
      ...nextOwnProps,
      itemUids: results,
      isLoadMoreShown,
      isLoading,
      hiddenRepliesUids,
      commentsCount,
      onLoadMore: () => dispatch(getCommentsActionCreator.request({ postUid, ordering: undefined })),
    })

    return prevProps
  }
}

const FeedPostCommentListContainer = connectOptionallyAdvanced<
  FeedPostCommentListProps, FeedPostCommentListOwnProps, State
>(selectorFactory)(FeedPostCommentList)

export default FeedPostCommentListContainer
